import { Component, ElementRef, Input, OnDestroy, TemplateRef, ViewChild } from '@angular/core';

import { UiCommand, UiCommandEvent, UiCommandSourceEnum, UiKeyboardShortcut } from '@ui/cer-ui-types';

import { CerFormView } from '@form/cer-form-view';
import { CerFormToolbar } from '@form/cer-form-toolbar';
import { ICerFormViewType } from '@form/cer-form-view-type/cer-form-view-type.interface';
import { PdfViewerComponent } from 'ng2-pdf-viewer';
import { Subscription } from 'rxjs';
import { uiCmdComponentMaximize, uiCmdDocumentZoomIn, uiCmdDocumentZoomOut } from '@cer/cer-ui/cer-ui-commands';
import { CerToolbarItemState } from '@cer/cer-toolbar/cer-toolbar-command-adapter';
import { documentToolbarItemsStateGet, documentTypeByExtension, fileNameExtensionGet } from './cer-form-view-document-type';

@Component({
  selector: 'cer-form-view-type-document-pdf',
  template: `<div *ngIf="hasToolbar" class="cer-flex-static cer-overflow-hidden" #toolbarDiv></div>
    <pdf-viewer #pdfViewer class="cer-flex-main cer-overflow-auto" id="pdfViewer" [src]="documentUrl" zoom-scale="page-width" [original-size]="false"></pdf-viewer>`
})

export class CerFormViewTypeDocumentPdfComponent implements ICerFormViewType, OnDestroy {

  private toolbarBuildIn: UiCommand[] = [uiCmdDocumentZoomIn, uiCmdDocumentZoomOut];

  private subscriptionManager$: Subscription = new Subscription();

  public ngOnDestroy(): void {
    this.subscriptionManager$.unsubscribe();
  }

  private _view: CerFormView;
  get view(): CerFormView { return this._view; };
  @Input() set view(view: CerFormView) {
    if (view && this._view != view) {
      this._view = view;
      view.commandSubscribe((e) => this.onCommand(e));
      this.subscriptionManager$.add(
        view.routeOptions.context.formService.formCommand$.subscribe(e => this.onCommand(e))
      );
      this.documentUrl = this._view.documentUrl;
    }
  }

  private _documentUrl!: string;
  get documentUrl(): string { return this._documentUrl };
  @Input() set documentUrl(documentUrl: string) {
    if (documentUrl != this._documentUrl) {
      const ext = fileNameExtensionGet(this.view?.documentFileName);
      const documentType = documentTypeByExtension(ext);
      if (documentType == 'pdf') {
        this._documentUrl = documentUrl;
        this.toolbarUpdate();
      }
    }
  }

  private toolbarUpdate() {
    this.toolbar?.itemStateApply();
  }

  @ViewChild(TemplateRef<any>) template!: TemplateRef<any>;

  private pdfViewer: PdfViewerComponent;
  @ViewChild('pdfViewer', { static: false }) set pdfViewerContent(pdfViewer: PdfViewerComponent) {
    if (pdfViewer) {
      this.pdfViewer = pdfViewer;
      this.pdfViewer.afterLoadComplete.subscribe((e) => {
        this.autoResize();
      });
    }
  }

  public onCommand(e: UiCommandEvent) {
    switch (e.commandId) {
      case uiCmdComponentMaximize.id:
        setTimeout(() => this.autoResize(), 100);
        break;
      case uiCmdDocumentZoomIn.id:
        this.zoomIn();
        break;
      case uiCmdDocumentZoomOut.id:
        this.zoomOut();
        break;
    }
    if (e.source == UiCommandSourceEnum.DesignChange) {
      this.autoResize();
    }
  }

  // #region Toolbar
  protected hasToolbar: boolean = true;
  private toolbar: CerFormToolbar;
  @ViewChild('toolbarDiv') set toolbarDivRefSet(ele: ElementRef<HTMLDivElement>) {
    const divParent = ele?.nativeElement;
    if (divParent != null && this.toolbar == null) {
      this.toolbar = new CerFormToolbar(divParent, this._view, this.toolbarBuildIn, this.toolbarItemStatesGet.bind(this));
      this.hasToolbar = this.toolbar != null;
    }
  }

  private toolbarItemStatesGet(): Map<string, CerToolbarItemState> {
    return documentToolbarItemsStateGet(true, true, this.documentUrl, this.view?.documentOriginalUrl, this.view?.documentOriginalFileName)
  }

  // #endregion
  public autoResize() {
    this.updateSize(0.95);
    //this.pdfViewer.afterLoadComplete
  }

  public updateSize(zoom: number, waitForPage: number = 10) {
    if (this.obj()) {
      this.pdfViewer.zoom = zoom;
      this.pdfViewer.updateSize();
    }
    else if (waitForPage > 0) {
      waitForPage--;
      setTimeout(() => { this.updateSize(zoom, waitForPage) }, 100);
    }
  }

  private obj(): any {
    return (<any>this.pdfViewer)?._pdf;
  }

  public zoomIn() {
    if (this.pdfViewer) {
      const zoom = (this.pdfViewer.zoom ?? 1.00) + 0.15;
      this.updateSize(zoom);
    }
  }

  public zoomOut() {
    if (this.pdfViewer) {
      const zoom = (this.pdfViewer.zoom ?? 1.00) - 0.15;
      this.updateSize(zoom);
    }
  }
}